import { create } from 'zustand';
import { apiClient } from '@/shared/api/client';
import { useAuthStore } from '@/shared/stores/auth';

/**
 * מצב השיחה החי של הנציג — כפי שה-PBX רואה אותו, לא כפי שהדפדפן רואה אותו.
 *
 * ה-sipStore יודע רק על שיחות שעברו דרך JsSIP בטאב הזה. שיחה שמצלצלת בטלפון
 * שולחני, שיחה שהתחילה לפני רענון הדף, או שיחת קמפיין שה-ARI חיבר ישירות —
 * לא קיימות שם. השרת (LiveCallTracker) עוקב אחרי אירועי ARI ומחזיק לכל
 * נציג "מצלצל/פעיל/לא נענה", וה-store הזה משקף אותו:
 *   1. SSE על /agent/live/stream (fetch עם Authorization — EventSource לא
 *      יודע לשלוח כותרות), עם חיבור מחדש אוטומטי.
 *   2. polling של /agent/live כל 15 שניות כרשת ביטחון כשהזרם לא מחובר.
 */
export interface LiveIncoming {
  channel_id: string;
  peer_channel_id?: string;
  from: string;
  did?: string;
  remote_name?: string;
  via?: string;
  since?: string;
  call_id?: number | null;
}

export interface LiveActive {
  channel_id: string;
  peer_channel_id?: string;
  direction: 'inbound' | 'outbound';
  status: string;
  remote: string;
  did?: string;
  call_id?: number | null;
  started_at?: string | null;
  answered_at?: string | null;
  source?: 'pbx' | 'call_row';
}

export interface LiveMissed {
  from: string;
  did?: string;
  status: string;
  at: string;
}

export interface LiveSnapshot {
  now?: string;
  incoming: LiveIncoming | null;
  active: LiveActive | null;
  last_missed: LiveMissed | null;
}

interface LiveCallState {
  incoming: LiveIncoming | null;
  active: LiveActive | null;
  lastMissed: LiveMissed | null;
  /** הזרם (SSE) מחובר כרגע. */
  connected: boolean;
  updatedAt: number | null;

  apply: (snap: LiveSnapshot) => void;
  refresh: () => Promise<void>;
  start: () => void;
  stop: () => void;
  hangup: (which?: 'incoming' | 'active' | 'any') => Promise<boolean>;
}

const POLL_MS = 15_000;
const RECONNECT_MIN_MS = 500;
const RECONNECT_MAX_MS = 15_000;

let abort: AbortController | null = null;
let pollTimer: number | undefined;
let reconnectTimer: number | undefined;
let backoff = RECONNECT_MIN_MS;
let running = false;

/** מפרק זרם SSE לאירועי data — מיוצא כדי שהטסטים יכסו את הפרסור בלי רשת. */
export function* parseSseChunk(buffer: { text: string }, chunk: string): Generator<string> {
  buffer.text += chunk;
  let idx: number;
  while ((idx = buffer.text.indexOf('\n\n')) >= 0) {
    const frame = buffer.text.slice(0, idx);
    buffer.text = buffer.text.slice(idx + 2);
    const data = frame
      .split('\n')
      .filter((l) => l.startsWith('data:'))
      .map((l) => l.slice(5).trim())
      .join('\n');
    if (data) yield data;
  }
}

export const useLiveCall = create<LiveCallState>((set, get) => ({
  incoming: null,
  active: null,
  lastMissed: null,
  connected: false,
  updatedAt: null,

  apply: (snap) =>
    set({
      incoming: snap.incoming ?? null,
      active: snap.active ?? null,
      lastMissed: snap.last_missed ?? null,
      updatedAt: Date.now(),
    }),

  refresh: async () => {
    try {
      const { data } = await apiClient.get<{ success: boolean; data: LiveSnapshot }>('/agent/live');
      get().apply(data.data);
    } catch {
      /* רשת — הניסיון הבא יתקן */
    }
  },

  start: () => {
    if (running) return;
    running = true;
    backoff = RECONNECT_MIN_MS;
    void get().refresh();
    void connect(set, get);
    pollTimer = window.setInterval(() => {
      if (!get().connected) void get().refresh();
    }, POLL_MS);
  },

  stop: () => {
    running = false;
    abort?.abort();
    abort = null;
    if (pollTimer !== undefined) window.clearInterval(pollTimer);
    if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
    pollTimer = reconnectTimer = undefined;
    set({ connected: false });
  },

  hangup: async (which = 'any') => {
    try {
      const { data } = await apiClient.post<{ success: boolean; data: { hung_up: boolean } }>(
        '/agent/live/hangup',
        { which },
      );
      // לא מחכים לאירוע ה-ARI — הנציג לחץ "נתק", המסך משקף את זה מיד.
      set(which === 'incoming' ? { incoming: null } : which === 'active' ? { active: null } : { incoming: null, active: null });
      void get().refresh();
      return data.data.hung_up;
    } catch {
      return false;
    }
  },
}));

async function connect(
  set: (p: Partial<LiveCallState>) => void,
  get: () => LiveCallState,
): Promise<void> {
  if (!running) return;
  const { token, tenantSlug } = useAuthStore.getState();
  if (!token) return;

  abort = new AbortController();
  const base = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    Authorization: `Bearer ${token}`,
  };
  if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;

  try {
    const resp = await fetch(`${base}/agent/live/stream`, { headers, signal: abort.signal });
    if (!resp.ok || !resp.body) throw new Error(`stream ${resp.status}`);
    set({ connected: true });
    backoff = RECONNECT_MIN_MS;

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    const buf = { text: '' };
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      for (const data of parseSseChunk(buf, decoder.decode(value, { stream: true }))) {
        try {
          const parsed = JSON.parse(data) as { data: LiveSnapshot };
          get().apply(parsed.data);
        } catch {
          /* מסגרת פגומה — מדלגים */
        }
      }
    }
    // השרת סוגר את הזרם כל ~2 דקות בכוונה (תקרת PHP) — מתחברים מיד.
    scheduleReconnect(set, get, RECONNECT_MIN_MS);
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') return;
    scheduleReconnect(set, get, backoff);
    backoff = Math.min(backoff * 2, RECONNECT_MAX_MS);
  } finally {
    set({ connected: false });
  }
}

function scheduleReconnect(
  set: (p: Partial<LiveCallState>) => void,
  get: () => LiveCallState,
  ms: number,
): void {
  if (!running) return;
  if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
  reconnectTimer = window.setTimeout(() => void connect(set, get), ms);
}
